import React, {
	type FC,
	useContext,
	useEffect,
	useState,
	useCallback,
	useRef,
	useLayoutEffect,
} from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { SpotlightManager, SpotlightTarget, SpotlightTransition } from '@atlaskit/onboarding';
import { token } from '@atlaskit/tokens';
import LockUnlocked from '@atlaskit/icon/core/lock-unlocked';
import { SplitButton } from '@atlaskit/button/new';

import { useSpaceKey } from '@confluence/space-utils/entry-points/useSpaceKey.ts';
import { fg } from '@confluence/feature-gating';
import { expVal, expValEquals, manuallyLogExpExposure } from '@confluence/feature-experiments';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import {
	useSetOnboardingState,
	useGetOnboardingState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState.ts';
import { ExperienceTrackerContext } from '@confluence/experience-tracker/entry-points/component.ts';
import {
	SHARE_AND_RESTRICT_BUTTON_LOAD_EXPERIENCE,
	SHARE_AND_RESTRICT_DIALOG_LOAD_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { useCollabNetworkStatus } from '@confluence/collab-network-status';
import { withFlags } from '@confluence/flags/entry-points/withFlags.ts';
import { type FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import {
	createSingleQueryParamHook,
	useRouteActions,
} from '@confluence/route-manager/entry-points/RouteState.ts';
import { RequestAccessPopupComponent } from '@confluence/grant-access-dialog/entry-points/RequestAccessPopupLoader.ts';
import { GrantAccessDialogLoader } from '@confluence/grant-access-dialog/entry-points/GrantAccessDialogLoader.ts';
import { GrantAccessFlagLoader } from '@confluence/grant-access-dialog/entry-points/GrantAccessFlagLoader.tsx';
import { useShouldShowGrantAccess } from '@confluence/grant-access-dialog/entry-points/useShouldShowGrantAccess.ts';
import { PageSegmentLoadEnd } from '@confluence/browser-metrics/entry-points/PageSegmentLoadEnd.ts';
import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import {
	SHARE_PAGE_SHORTCUT,
	UNIVERSAL_SHARE_PAGE_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import {
	CopyLinkButton,
	type CopyLinkProps,
} from '@confluence/copy-content-link/entry-points/CopyLinkButton.ts';
import { useGetPageMode } from '@confluence/page-utils/entry-points/useGetPageMode.ts';
import { PageMode } from '@confluence/page-utils/entry-points/enums.ts';
import { useIsSuperAdmin } from '@confluence/super-admin/entry-points/AdminKeyQuery.ts';
import { ONBOARDING_SHARE_AND_RESTRICT_DIALOG } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants.ts';
import { ExperimentExportJiraContextToPageNudgeSpotlightStepWrapper } from '@confluence/experiment-export-jira-context-nudge/entry-points/spotlight-tour.ts';
import { NewAccessRequestIndicator } from '@confluence/access-requests/entry-points/NewAccessRequestIndicator.tsx';
import { RealtimeAccessRequests } from '../controlled/realtimeAccessRequests/RealtimeAccessRequests.tsx';
import { ControlledShareButton } from '../controlled/ControlledShareButton.tsx';
import {
	Controller,
	SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
	SAC_CONTENT_PERMS_MANAGEMENT_DOCUMENTATION,
} from '../controller/Controller.tsx';
import { SHARE_BUTTON_PAGE_SEGMENT_LOAD } from '../perf.config.ts';
import type { DialogVariant } from '../controller/masterState.ts';

import { useShareAndRestrictButtonData } from './useShareAndRestrictButtonData.ts';
import { useShareAndRestrictButtonActions } from './useShareAndRestrictButtonState.ts';
import { useIsNewUser } from './useIsNewUser.tsx';
import { ShareAndRestrictButtonPlaceholder } from './ShareAndRestrictButtonPlaceholder.tsx';
import { OnboardingPagePermissionsSpotlightLoader } from './OnboardingPagePermissionsSpotlight.tsx';
import { TwoStageHydration } from '@confluence/ssr-utilities/entry-points/TwoStageHydration.ts';

// GALILEO-5536 START
// GALILEO-5536 END

const PUBLIC_LINK_ENROLLED_CONTENT_TYPES: ContentType[] = ['page', 'whiteboard'];

export const i18n = defineMessages({
	shareButtonSpotlightHeading: {
		id: 'share-and-restrict-dialog.spotlight.heading',
		defaultMessage: 'Restrictions have moved',
		description:
			'Spotlight heading for the Share and Restrict dialog, to be shown when users get the new feature',
	},
	shareButtonSpotlightText: {
		id: 'share-and-restrict-dialog.spotlight.text',
		defaultMessage: 'Now you can share and restrict access to your content in one place.',
		description:
			'Spotlight text for the Share and Restrict dialog, to be shown when users get the new feature',
	},
	shareButtonSpotlightLearnMore: {
		id: 'share-and-restrict-dialog.spotlight.learn-more',
		defaultMessage: 'Learn more',
		description: 'Link to learn more in the spotlight',
	},
	shareButtonSpotlightOk: {
		id: 'share-and-restrict-dialog.spotlight.ok',
		defaultMessage: 'OK',
		description: 'Action OK to close the spotlight',
	},
	shareButtonChangeBoardingFlagHeading: {
		id: 'share-and-restrict-dialog.flag.heading',
		defaultMessage: 'Your lock icon has moved',
		description:
			'Heading of the new bottom left corner flag that leads to the spotlight around the Share and Restrict button',
	},
	shareButtonChangeBoardingFlagDescription: {
		id: 'share-and-restrict-dialog.flag.message',
		defaultMessage:
			'The lock icon (where you manage access to your content) is now in the Share button.',
		description:
			'Body of the new bottom left corner flag that leads to the spotlight around the Share and Restrict button',
	},
	shareButtonChangeBoardingFlagAction: {
		id: 'share-and-restrict-dialog.flag.action',
		defaultMessage: 'Show me',
		description:
			'Call to action text of the new bottom left corner flag that leads to the spotlight around the Share and Restrict button',
	},
});

const useAccountIdQueryParam = createSingleQueryParamHook('accountId');
const useUsernameQueryParam = createSingleQueryParamHook('username');
const useGrantAccessQueryParam = createSingleQueryParamHook('grantAccess');
const useAccessTypeQueryParam = createSingleQueryParamHook('accessType');

export type ShareeAction = 'view' | 'edit';

export type ContentType =
	| 'page'
	| 'blogpost'
	| 'space'
	| 'whiteboard'
	| 'database'
	| 'embed'
	| 'folder';

export type ContentSubType = 'live' | 'embedded';

export type ShareAndRestrictButtonProps = {
	contentId: string;
	contentType: ContentType;
	flags: FlagsStateContainer;
	allowRestrictions?: boolean;
	shareeAction?: ShareeAction;
	contentSubType?: ContentSubType;
	isDisabled?: boolean;
	isUnpublishedDraft?: boolean;
};

interface SeparateDisabledStates {
	isCopyLinkButtonDisabled?: boolean;
	isShareAndRestrictButtonDisabled?: boolean;
}

/**
 * Excluding spaceKey and pageMode here. These are obtained directly from hooks
 * inside the ShareRestrictAndCopyCoordinator component to avoid bloating host components.
 */
export type ShareRestrictAndCopyCoordinatorProps = Omit<
	ShareAndRestrictButtonProps & CopyLinkProps & SeparateDisabledStates,
	'spaceKey' | 'pageMode'
>;

const convertPageMode = (mode: PageMode) => (mode === PageMode.LIVE ? PageMode.VIEW : mode);

const CHANGEBOARDING_FLAG_ID = 'share-restrict-button-changeboarding-flag';

const ShareRestrictAndCopyCoordinatorComponent: FC<ShareRestrictAndCopyCoordinatorProps> = ({
	contentId,
	shareeAction = 'view',
	allowRestrictions = true,
	contentType,
	contentSubType,
	isUnpublishedDraft = false,
	flags,

	// Separate disabled states
	isShareAndRestrictButtonDisabled = false,
	isCopyLinkButtonDisabled = false,

	// CopyLinkButton props
	...copyLinkButtonProps
}) => {
	const { formatMessage } = useIntl();
	const [controllerMounted, setControllerMounted] = useState(false);
	const experienceTracker = useContext(ExperienceTrackerContext);
	const { edition } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [isSpotlightActive, setIsSpotlightActive] = useState(false);
	// Grant Access query params
	const accountId = useAccountIdQueryParam();
	const username = useUsernameQueryParam();
	const grantAccess = useGrantAccessQueryParam();
	const accessType = useAccessTypeQueryParam();
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const [{ isCollabOffline }] = useCollabNetworkStatus();

	// Network status is used when:
	// - isCollabOffline: The collab provider has detected a network disconnection
	// - platform_editor_offline_editing_web: Parent experiment for offline editing experience (must be enabled)
	// - platform_editor_offline_editing_wrapper_override: Kill switch to disable this feature (must be disabled)
	const isNetworkDisabled =
		isCollabOffline &&
		expVal('platform_editor_offline_editing_web', 'isEnabled', false) &&
		!fg('platform_editor_offline_editing_wrapper_override');

	// Hook to store share button click state
	const { setHasClickedShareButton, resetHasClickedShareButton } =
		useShareAndRestrictButtonActions();

	// Create a ref for the share button
	const shareButtonRef = useRef<HTMLButtonElement>(null);

	const shouldShowGrantAccessDialog = (grantAccess && accountId) || username;

	const isContentTypePublicLinkEligible = PUBLIC_LINK_ENROLLED_CONTENT_TYPES.includes(contentType);

	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();
	const { isSuperAdmin } = useIsSuperAdmin();

	const {
		// Used here for rendering the button
		icon,
		isLoading,
		error,
		tooltipMessage,
		refetch,
		// Used here for analytics
		hasInheritedRestrictions,
		hasRestrictions,
		hasViewRestrictions,
		isPublicLinkOn,
		pageTitle,
	} = useShareAndRestrictButtonData(contentId, isContentTypePublicLinkEligible, isUnpublishedDraft);

	const { isNewUser } = useIsNewUser();

	const { setQueryParams } = useRouteActions();

	const dismissGrantAccessDialog = useCallback(() => {
		setQueryParams(
			{
				grantAccess: undefined,
				accessType: undefined,
				accountId: undefined,
				username: undefined,
			},
			true,
		);
	}, [setQueryParams]);

	const handleClick = useCallback(() => {
		if (renderServerPlaceholder) {
			return;
		} else {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					actionSubject: 'button',
					actionSubjectId: 'ShareAndRestrictButton',
					action: 'clicked',
					attributes: {
						contentId,
						contentSubType,
						isLivePage: contentSubType === 'live',
						isDraft: isUnpublishedDraft,
						contentType,
						edition,
						hasInheritedRestrictions,
						hasRestrictions,
						hasViewRestrictions,
						isPublicLinkOn,
						pageMode: shareeAction,
					},
				},
			}).fire();
			refetch();
			setControllerMounted(true);
			//mark has share button click to true
			void setHasClickedShareButton();

			//start share and restrict dialog load experience
			experienceTracker.start({
				name: SHARE_AND_RESTRICT_DIALOG_LOAD_EXPERIENCE,
				timeout: ExperienceTimeout.DEFAULT,
			});
		}
	}, [
		createAnalyticsEvent,
		contentId,
		contentSubType,
		contentType,
		edition,
		hasInheritedRestrictions,
		hasRestrictions,
		hasViewRestrictions,
		isUnpublishedDraft,
		isPublicLinkOn,
		refetch,
		shareeAction,
		experienceTracker,
		renderServerPlaceholder,
		setHasClickedShareButton,
	]);

	useEffect(() => {
		void resetHasClickedShareButton();
	}, [resetHasClickedShareButton]);

	useEffect(() => {
		createAnalyticsEvent({
			type: 'sendScreenEvent',
			data: {
				name: 'ShareAndRestrictButton',
				attributes: {
					contentId,
					contentType,
					contentSubType,
					isLivePage: contentSubType === 'live',
					isDraft: isUnpublishedDraft,
					pageMode: shareeAction,
				},
			},
		}).fire();
	}, [
		createAnalyticsEvent,
		contentId,
		contentType,
		contentSubType,
		isUnpublishedDraft,
		shareeAction,
	]);

	useEffect(() => {
		if (!isLoading && !error) {
			experienceTracker.succeed({
				name: SHARE_AND_RESTRICT_BUTTON_LOAD_EXPERIENCE,
			});
		} else if (!isLoading && error) {
			experienceTracker.fail({ error, name: SHARE_AND_RESTRICT_BUTTON_LOAD_EXPERIENCE });
		}
	}, [error, isLoading, experienceTracker]);

	useEffect(() => {
		if (edition !== ConfluenceEdition.FREE) {
			manuallyLogExpExposure('confluence_contextual_ask_to_edit');
		}
	}, [edition]);

	const {
		data,
		loading,
		error: spotlightError,
	} = useGetOnboardingState(ONBOARDING_SHARE_AND_RESTRICT_DIALOG.DIALOG_SPOTLIGHT_SEEN);
	const { setOnboardingState } = useSetOnboardingState();

	useEffect(() => {
		if (data && !loading && !spotlightError && !error) {
			const spotlightSeen =
				data?.onboardingState?.find(
					(prop) => prop.key === ONBOARDING_SHARE_AND_RESTRICT_DIALOG.DIALOG_SPOTLIGHT_SEEN,
				)?.value === 'true';
			// Live pages does not want users to see USD button change-boarding
			if (!(contentSubType == 'live' || isNewUser)) {
				// If the spotlight hasn't been seen before
				if (!spotlightSeen) {
					const markSpotlightAsSeen = () =>
						setOnboardingState({
							key: ONBOARDING_SHARE_AND_RESTRICT_DIALOG.DIALOG_SPOTLIGHT_SEEN,
							value: 'true',
						});

					const onFlagDismissClick = () => {
						markSpotlightAsSeen();
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
								actionSubject: 'ShareAndRestrictButton',
								action: 'flag dismissed',
								attributes: {
									contentId,
									contentSubType,
									isLivePage: false,
									contentType,
									edition,
									hasInheritedRestrictions,
									hasRestrictions,
									hasViewRestrictions,
									isPublicLinkOn,
									pageMode: shareeAction,
								},
							},
						}).fire();
					};

					const onFlagShowMeClick = () => {
						setIsSpotlightActive(true);
						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
								actionSubject: 'ShareAndRestrictButton',
								action: 'spotlight shown',
								attributes: {
									contentId,
									contentSubType,
									isLivePage: false,
									contentType,
									edition,
									hasInheritedRestrictions,
									hasRestrictions,
									hasViewRestrictions,
									isPublicLinkOn,
									pageMode: shareeAction,
								},
							},
						}).fire();
						void flags.hideFlag(CHANGEBOARDING_FLAG_ID);
						markSpotlightAsSeen();
					};

					const showFlag = async () => {
						await flags.showFlag({
							id: CHANGEBOARDING_FLAG_ID,
							type: 'custom',
							title: formatMessage(i18n.shareButtonChangeBoardingFlagHeading),
							description: formatMessage(i18n.shareButtonChangeBoardingFlagDescription),
							actions: [
								{
									content: formatMessage(i18n.shareButtonChangeBoardingFlagAction),
									onClick: onFlagShowMeClick,
								},
							],
							customIcon: <LockUnlocked label="" />,
							onClose: onFlagDismissClick,
							close: 'auto',
						});

						createAnalyticsEvent({
							type: 'sendTrackEvent',
							data: {
								source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
								actionSubject: 'ShareAndRestrictButton',
								action: 'flag shown',
								attributes: {
									contentId,
									contentSubType,
									isLivePage: false,
									contentType,
									edition,
									hasInheritedRestrictions,
									hasRestrictions,
									hasViewRestrictions,
									isPublicLinkOn,
									pageMode: shareeAction,
								},
							},
						}).fire();
					};

					void showFlag();
				}
			}
		} else if (spotlightError && !loading) {
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
					actionSubject: 'ShareAndRestrictButton',
					action: 'spotlight failed',
					attributes: {
						contentId,
						contentSubType,
						isLivePage: contentSubType === 'live',
						contentType,
						edition,
						hasInheritedRestrictions,
						hasRestrictions,
						hasViewRestrictions,
						isPublicLinkOn,
						pageMode: shareeAction,
					},
				},
			}).fire();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, loading, spotlightError, error]);

	const endSpotlight = () => {
		// Change spotlight state to only show once
		void setOnboardingState({
			key: ONBOARDING_SHARE_AND_RESTRICT_DIALOG.DIALOG_SPOTLIGHT_SEEN,
			value: 'true',
		});
		setIsSpotlightActive(false);
	};

	const { Spotlight } = useChoreographerAssets();

	const learnMoreClicked = () => {
		endSpotlight();
		window.open(SAC_CONTENT_PERMS_MANAGEMENT_DOCUMENTATION, '_blank', 'noreferrer');
	};

	const renderControlledShareButton = () => {
		const indicatorRightOffset = icon === 'public' ? 54 : 55;
		const button = (
			<NewAccessRequestIndicator
				top={4}
				right={indicatorRightOffset}
				source={SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE}
			>
				<ControlledShareButton
					tooltipMessage={tooltipMessage}
					icon={icon}
					onClick={handleClick}
					isDisabled={isShareAndRestrictButtonDisabled || isNetworkDisabled}
					focusRef={shareButtonRef}
				/>
			</NewAccessRequestIndicator>
		);
		// GALILEO-5536 START
		if (fg('twcg-392_export_jira_context_to_page_killswitch')) {
			return (
				<ExperimentExportJiraContextToPageNudgeSpotlightStepWrapper stepIndex={1}>
					{button}
				</ExperimentExportJiraContextToPageNudgeSpotlightStepWrapper>
			);
		}
		// GALILEO-5536 END
		return button;
	};

	const renderGrantAccessFlagAndUsdButton = () => (
		<>
			<GrantAccessFlagLoader contentId={contentId} />
			{renderControlledShareButton()}
		</>
	);

	const renderGrantAccessDialogAndUsdButton = () =>
		shouldShowGrantAccessDialog ? (
			<GrantAccessDialogLoader
				accessType={accessType === 'edit' ? 'edit' : 'view'}
				accountId={accountId || username || ''}
				buttonPlaceholder={<ShareAndRestrictButtonPlaceholder />}
				contentId={contentId}
				isOpen={!renderServerPlaceholder}
				onActionClick={dismissGrantAccessDialog}
				shouldSSRGrantAccessRestrictions={fg('confluence_frontend_usd_ssr')}
			>
				{renderControlledShareButton()}
			</GrantAccessDialogLoader>
		) : (
			<>{renderControlledShareButton()}</>
		);

	const pageMode = convertPageMode(useGetPageMode());
	const spaceKey = useSpaceKey() ?? '';

	const { shouldShowGrantAccess, clearGrantAccessQueryParams } = useShouldShowGrantAccess();
	const [initialDialog, setInitialDialog] = useState<DialogVariant | undefined>(
		shouldShowGrantAccess &&
			edition !== 'FREE' &&
			fg('confluence_frontend_stateful_access_requests')
			? 'accessRequests'
			: undefined,
	);

	useLayoutEffect(() => {
		if (initialDialog) {
			setControllerMounted(true);
		}
	}, [initialDialog]);

	const openAccessRequestsDialog = useCallback(() => {
		setInitialDialog('accessRequests');
	}, []);

	const clearInitialDialog = useCallback(() => {
		setInitialDialog(undefined);
		clearGrantAccessQueryParams();
	}, [clearGrantAccessQueryParams]);

	const renderGrantAccessFlagOrDialog = () =>
		fg('confluence_frontend_grant_access_flag')
			? renderGrantAccessFlagAndUsdButton()
			: renderGrantAccessDialogAndUsdButton();

	return (
		<TwoStageHydration
			name="share-button"
			enabled={expValEquals('platform_editor_hydratable_ui', 'isEnabled', true)}
		>
			<div data-vc="share-restrict-and-copy-button">
				<SplitButton>
					<>
						{isLoading ? (
							<ShareAndRestrictButtonPlaceholder />
						) : (
							<>
								<SpotlightTarget name="share-button-spotlight-target">
									<SpotlightManager>
										<SpotlightTarget name="shareAndRestrictButton">
											{/* Request access flag */}
											<RequestAccessPopupComponent contentId={contentId} />
											{/*
											If Access Requests is live, it replaces any form of Grant Access Flag or Dialog
											We only render the share button for Access Requests, bc the experience itself is inside USD
											*/}
											{fg('confluence_frontend_stateful_access_requests')
												? renderControlledShareButton()
												: renderGrantAccessFlagOrDialog()}
										</SpotlightTarget>

										<SpotlightTransition>
											{isSpotlightActive && (
												<Spotlight
													actions={[
														{
															onClick: endSpotlight,
															text: formatMessage(i18n.shareButtonSpotlightOk),
														},
														{
															onClick: learnMoreClicked,
															text: formatMessage(i18n.shareButtonSpotlightLearnMore),
														},
													]}
													heading={formatMessage(i18n.shareButtonSpotlightHeading)}
													target="shareAndRestrictButton"
													key="shareAndRestrict-spotlight"
													targetRadius={3}
													targetBgColor={token('elevation.surface')}
													dialogPlacement="bottom right"
													dialogWidth={275}
												>
													<FormattedMessage
														values={{
															bold: (chunks: React.ReactNode[]) => <b>{chunks}</b>,
														}}
														{...i18n.shareButtonSpotlightText}
													/>
												</Spotlight>
											)}
										</SpotlightTransition>
									</SpotlightManager>
								</SpotlightTarget>

								<OnboardingPagePermissionsSpotlightLoader />

								<GeneralShortcutListener
									deprecatedAccelerator={
										!(isLiveEditMode || isOnEditRoute) ? SHARE_PAGE_SHORTCUT : undefined
									}
									accelerator={UNIVERSAL_SHARE_PAGE_SHORTCUT}
									listener={handleClick}
								/>
							</>
						)}
						{!isLoading && (
							<PageSegmentLoadEnd
								metric={SHARE_BUTTON_PAGE_SEGMENT_LOAD}
								key={contentId}
								customData={{ shareButtonType: 'ShareAndRestrictButton' }}
							/>
						)}
					</>
					<CopyLinkButton
						contentId={contentId}
						contentType={contentType}
						pageMode={pageMode}
						spaceKey={spaceKey}
						isDisabled={isCopyLinkButtonDisabled}
						{...copyLinkButtonProps}
					/>
				</SplitButton>
			</div>
			{controllerMounted && (
				<Controller
					contentId={contentId}
					contentType={contentType}
					contentSubType={contentSubType}
					allowRestrictions={allowRestrictions}
					pageTitle={pageTitle}
					pageMode={shareeAction}
					refetchButtonData={refetch}
					onClose={() => {
						setControllerMounted(false);
					}}
					isUnpublishedDraft={isUnpublishedDraft}
					focusRef={shareButtonRef}
					hasRestrictions={hasRestrictions}
					hasInheritedRestrictions={hasInheritedRestrictions}
					hasViewRestrictions={hasViewRestrictions}
					isSuperAdmin={isSuperAdmin}
					initialDialog={initialDialog}
					onInitialDialogOpen={clearInitialDialog}
				/>
			)}

			<RealtimeAccessRequests
				contentId={contentId}
				contentType={contentType}
				pageTitle={pageTitle}
				onReviewRequestsClick={openAccessRequestsDialog}
			/>
		</TwoStageHydration>
	);
};

export const ShareRestrictAndCopyCoordinator = withFlags(ShareRestrictAndCopyCoordinatorComponent);
